import { useTranslations } from '@vocab/react';
import { Columns, Column, Stack, Text } from 'braid-design-system';
import type { Maybe } from 'graphql/jsutils/Maybe';
import React, {
  useRef,
  type Ref,
  forwardRef,
  memo,
  type RefObject,
  useImperativeHandle,
  useEffect,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { UpsellPanel } from 'src/app/shared/components/UpsellPanel';
import { SECTION_STO_UPSELL } from 'src/app/shared/constants/pageSectionName';
import {
  actionResetProductDisplayName,
  actionUpdateProductDisplayName,
} from 'src/app/v1/state/modules/formData/productDisplayName';
import { actionUpdateBuyUpSell } from 'src/app/v1/state/modules/session/buyUpSell';
import {
  actionRemoveCartLineItem,
  actionUpdateCartLineItem,
} from 'src/app/v1/state/modules/session/cart';
import { actionUpdateEditFlowCheckout } from 'src/app/v1/state/modules/session/editFlowCheckout';
import type { CartLineItem } from 'src/app/v1/state/types/cart';
import { BrandingContextualHelp } from 'src/app/v1/views/components/BrandingContextualHelp';
import { KeySellingPoints } from 'src/app/v1/views/components/KeySellingPoints';
import { BrandingEditView } from 'src/app/v1/views/pages/Edit/components/BrandingEditView';
import { BrandingReadOnlyView } from 'src/app/v1/views/pages/Edit/components/BrandingReadOnlyView';
import { KeySellingPointsReadOnlyView } from 'src/app/v1/views/pages/Edit/components/KeySellingPointsReadOnlyView';
import { EditablePageSection } from 'src/app/v1/views/sections/EditablePageSection';
import type {
  AdProduct,
  AdProductUpgrade,
  CatalogueProductFeature,
} from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';
import type { KeySellingPointsRef } from 'src/types/formFields';
import type { SectionRef } from 'src/types/sections';

import translations from './.vocab';
import exampleBrandedAd from './example.png';
import { getPriceAndTaxSelector } from './utils/getPriceAndTaxSelector';

import * as styles from './index.css';

const Read = () => {
  const { t } = useTranslations(translations);

  return (
    <Stack space="large">
      <Stack space="medium">
        <Text weight="strong">
          {t('standout-upsell-section-read-edit-branding-label')}
        </Text>
        <BrandingReadOnlyView />
      </Stack>
      <KeySellingPointsReadOnlyView />
    </Stack>
  );
};

interface EditProps {
  refs: {
    keySellingPointsRef: RefObject<KeySellingPointsRef>;
  };
  upgradeFeatures?: Array<Maybe<CatalogueProductFeature>>;
}
const Edit = ({ refs, upgradeFeatures }: EditProps) => {
  const { isPrivateAdvertiser } = useSelector(
    (state: AppState) => ({
      isPrivateAdvertiser: state.session.hirerAccount?.isPrivateAdvertiser,
    }),
    shallowEqual,
  );
  const { t } = useTranslations(translations);

  // TODO: Make this logic into a util
  const isKeySellingPointsEnabled = upgradeFeatures
    ? upgradeFeatures.find((feature) => feature?.type === 'BulletPoints')
    : false;
  const isBrandingEnabled = upgradeFeatures
    ? upgradeFeatures.find((feature) => feature?.type === 'Branding')
    : false;

  return (
    <Stack space="medium">
      <Stack space="small">
        {isBrandingEnabled ? (
          <>
            <Columns space="none">
              <Column>
                <Text weight="strong">
                  {t('standout-upsell-section-read-edit-branding-label')}
                </Text>
              </Column>
              {isPrivateAdvertiser ? null : (
                <Column width="content">
                  <BrandingContextualHelp />
                </Column>
              )}
            </Columns>
            <BrandingEditView
              customFeatures={upgradeFeatures as AdProduct['features']}
            />{' '}
          </>
        ) : null}
      </Stack>
      {isKeySellingPointsEnabled ? (
        <KeySellingPoints ref={refs.keySellingPointsRef} />
      ) : null}
    </Stack>
  );
};

interface Props {
  upgradeItem: AdProductUpgrade;
}

export const StandOutUpsellSection = memo(
  forwardRef(({ upgradeItem }: Props, ref: Ref<SectionRef>) => {
    const { buyUpSell, cart, selectedProductId } = useSelector(
      (state: AppState) => ({
        buyUpSell: state.session.buyUpSell,
        cart: state.session.cart,
        selectedProductId: state.formData.selectedProductId,
      }),
      shallowEqual,
    );

    const { t } = useTranslations(translations);

    const editablePageSectionRef = useRef<SectionRef>(null);
    const keySellingPointsRef = useRef<KeySellingPointsRef>(null);

    const dispatch = useDispatch();

    const handleUpSellClick = () => {
      dispatch(actionUpdateBuyUpSell(!buyUpSell));
      dispatch(actionUpdateEditFlowCheckout({ enabled: !buyUpSell }));
    };

    useImperativeHandle(
      ref,
      () =>
        ({
          ...editablePageSectionRef.current,
          edit: buyUpSell,
        } as SectionRef),
    );

    useEffect(() => {
      if (buyUpSell && upgradeItem.displayMetadata?.name) {
        dispatch(
          actionUpdateProductDisplayName(upgradeItem.displayMetadata?.name),
        );
      } else if (!buyUpSell) {
        dispatch(actionResetProductDisplayName());
      }
    }, [buyUpSell, dispatch, upgradeItem.displayMetadata?.name]);

    useEffect(() => {
      const JOB_EDIT_PRODUCT_ID = selectedProductId;

      const lineItem = cart.lineItems.find(
        (item) => item.productId === JOB_EDIT_PRODUCT_ID,
      );

      const hasUpgradeItemInTheCart = () =>
        cart.lineItems.find((item) => item.productId === upgradeItem.id);

      if (buyUpSell && lineItem && !Boolean(hasUpgradeItemInTheCart())) {
        const cartItem: CartLineItem = {
          ...lineItem,
          productId: upgradeItem.id,
        };

        dispatch(actionUpdateCartLineItem(cartItem));
        return;
      }

      if (!buyUpSell && Boolean(hasUpgradeItemInTheCart())) {
        dispatch(actionRemoveCartLineItem(upgradeItem.id));
      }
    }, [
      buyUpSell,
      cart.lineItems,
      dispatch,
      selectedProductId,
      upgradeItem.id,
    ]);

    const priceAndTaxSelector = getPriceAndTaxSelector(upgradeItem);
    const productPrice = useSelector(priceAndTaxSelector, shallowEqual);

    const upgradeAdType = upgradeItem.displayMetadata?.name ?? '';

    const benefits =
      upgradeItem.displayMetadata?.sellingPoints?.map(
        (sellingPoint) => sellingPoint.name,
      ) ?? [];

    const imagePath =
      upgradeItem.displayMetadata?.image?.url ?? exampleBrandedAd;
    const imageDescription = upgradeItem.displayMetadata?.image?.description;

    const upgradeFeatures = upgradeItem.features;

    return (
      <>
        <UpsellPanel
          heading={t('standout-upsell-heading', { adType: upgradeAdType })}
          price={productPrice?.netPrice}
          tax={productPrice?.tax ?? undefined}
          benefits={benefits}
          columnWidth="3/5"
          image={imagePath}
          imageClassName={styles.image}
          imageDescription={imageDescription}
          isSelected={buyUpSell}
          onClick={handleUpSellClick}
          productPrice={productPrice}
        />
        {buyUpSell && (
          <EditablePageSection
            title={t('standout-upsell-section-title', {
              adType: upgradeAdType,
            })}
            name={SECTION_STO_UPSELL}
            ref={editablePageSectionRef}
            fields={[keySellingPointsRef]}
            read={<Read />}
            edit={
              <Edit
                refs={{ keySellingPointsRef }}
                upgradeFeatures={upgradeFeatures}
              />
            }
            defaultIsEdit
          />
        )}
      </>
    );
  }),
);
